import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomAlert from '../CustomAlert';
import QRCodeGenerator from '../QRCodeGenerator';
import { handlePrintNote } from '../../utils/printDeliveryNote';

const apiUrl = process.env.REACT_APP_API_URL;

const DeliveryNote = forwardRef(function DeliveryNote({ hideSidebar = false, editId: propEditId }, ref) {
  const [searchParams] = useSearchParams();
  const queryEditId = searchParams.get('id'); // Get ID from URL parameters
  const editId = propEditId || queryEditId; // Use prop ID first, then query parameter
  const [savedDeliveryNoteId, setSavedDeliveryNoteId] = useState(null); // Track ID for new delivery notes
  const [invoice, setInvoice] = useState({
    logo: 'https://elkos.mk/img/custom/logo_green.png', // Replace with your logo URL
    invoiceNumber: '001',
    date: new Date().toISOString().slice(0, 10),
    partner_id: null,
    recipient: '',
    recipientAddress: '',
    recipientBankNo: '',
    items: [{ id: 1, name: '', quantity: 1, price: 100, taxRate: 18 }],
    note: '',
    currency: 'ден',
  });

  // Business details state
  const [businessDetails, setBusinessDetails] = useState({
    website: '',
    address: '',
    phone: '',
    email: '',
    bank_account: '',
    full_name: 'ЕЛКОС ДООЕЛ Скопје',
    city: '',
    tax_id: ''
  });

  // Custom Alert State
  const [alert, setAlert] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    details: ''
  });
  

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({});

  // Partner search state
  const [suggestionsPartner, setSuggestionsPartner] = useState([]);
  const [showPartnerSuggestions, setShowPartnerSuggestions] = useState(false);
  const [queryPartner, setQueryPartner] = useState('');

  // Product search state
  const [suggestionsProduct, setSuggestionsProduct] = useState([]);
  const [productActiveRow, setProductActiveRow] = useState(null);

  const showAlert = (type, title, message, details = '') => {
    setAlert({
      isOpen: true,
      type,
      title,
      message,
      details
    });
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, isOpen: false }));
  };

  const handlePrintClick = () => {
    console.log('Invoice data:', invoice); // Debug: check if invoice is defined
    console.log('Edit ID:', editId); // Debug: check if editId is available
    console.log('Saved Delivery Note ID:', savedDeliveryNoteId); // Debug: check saved ID
    const deliveryNoteId = editId || savedDeliveryNoteId;
    // Print only - don't upload to server
    handlePrintNote(invoice, showAlert, closeAlert, 'delivery', false, deliveryNoteId);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!invoice.invoiceNumber?.trim()) {
      errors.invoiceNumber = 'Delivery note number is required';
    }
    
    if (!invoice.recipient?.trim()) {
      errors.recipient = 'Recipient name is required';
    }
    
    if (!invoice.date) {
      errors.date = 'Delivery date is required';
    }
    
    // Validate items - check for empty rows
    const hasEmptyRows = invoice.items.some(item => {
      const hasAnyData = item.name?.trim() || item.quantity;
      const isComplete = item.name?.trim() && 
                        item.quantity && 
                        parseFloat(item.quantity) > 0;
      
      // If row has some data but is not complete, it's an empty/incomplete row
      return hasAnyData && !isComplete;
    });
    
    const validItems = invoice.items.filter(item => 
      item.name?.trim() && 
      item.quantity && 
      parseFloat(item.quantity) > 0
    );
    
    if (validItems.length === 0) {
      errors.items = 'At least one valid item with name and quantity is required';
    } else if (hasEmptyRows) {
      errors.items = 'Please complete all item rows or remove empty rows before saving';
    }
    
    return errors;
  };

  // Function to fetch next suggested delivery note number
  const fetchNextInvoiceNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return; // Don't fetch if not logged in

      const response = await fetch(`${apiUrl}/dnote-next-number`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(prev => ({
          ...prev,
          invoiceNumber: data.nextNumber
        }));
      }
    } catch (error) {
      console.error('Failed to fetch next delivery note number:', error);
      // Keep default value on error
    }
  };

  // Function to fetch business details
  const fetchBusinessDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/business-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBusinessDetails({
          website: data.website || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          bank_account: data.bank_account || '',
          full_name: data.full_name || '',
          city: data.city || '',
          tax_id: data.tax_id || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch business details:', error);
    }
  };

  // Function to populate delivery note data for editing
  const populateDeliveryNoteData = (data) => {
    if (!data) return;

    // Format bank information: combine bank name and bank number
    let bankInfo = '';
    if (data.partner_bank && data.partner_bank_number) {
      bankInfo = `${data.partner_bank} - ${data.partner_bank_number}`;
    } else if (data.partner_bank_number) {
      bankInfo = data.partner_bank_number;
    } else if (data.partner_bank) {
      bankInfo = data.partner_bank;
    }

    setInvoice(prev => ({
      ...prev,
      invoiceNumber: data.dnote_number || prev.invoiceNumber,
      date: data.date || prev.date,
      partner_id: data.partner_id || null,
      recipient: data.partner_name || '',
      recipientAddress: data.partner_address || '',
      recipientBankNo: bankInfo,
      note: data.note || '',
      items: data.items && data.items.length > 0 
        ? data.items.map((item, index) => ({
            id: item.id || Date.now() + index,
            name: item.product_name || item.name || '',
            quantity: item.quantity || 1,
            price: item.product_price || 0,
            taxRate: item.product_tax || 0,
            product_id: item.product_id || null
          }))
        : prev.items
    }));

    // Also set the partner search query to show the partner name in the input field
    if (data.partner_name) {
      setQueryPartner(data.partner_name);
      
      // If we have partner data, create a partner suggestion for proper selection
      if (data.partner_id && data.partner_name) {
        const partnerData = {
          id: data.partner_id,
          name: data.partner_name,
          address: data.partner_address || '',
          phone: data.partner_phone || '',
          bank: data.partner_bank || '',
          bank_number: data.partner_bank_number || ''
        };
        setSuggestionsPartner([partnerData]);
      }
    }
  };

  // Function to fetch delivery note data for editing
  const fetchDeliveryNoteForEdit = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showAlert('error', 'Authentication Required', 'Please login to access this delivery note.');
        return;
      }

      const response = await fetch(`${apiUrl}/dnote-view/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        
        // The API returns { deliveryNote: {...}, items: [...] }
        // We need to combine them for populateDeliveryNoteData
        
        // Format date for HTML date input (YYYY-MM-DD)
        let formattedDate = '';
        if (responseData.deliveryNote.dnote_date) {
          const dateObj = new Date(responseData.deliveryNote.dnote_date);
          formattedDate = dateObj.toISOString().split('T')[0]; // Get YYYY-MM-DD format
        }
        
        const data = {
          ...responseData.deliveryNote,
          items: responseData.items || [],
          // Map the date field correctly
          date: formattedDate || responseData.deliveryNote.dnote_date
        };
        
        populateDeliveryNoteData(data);
        
        // showAlert('info', 'Delivery Note Loaded', 
        //   `Loaded delivery note #${data.dnote_number || id} for editing`,
        //   `Partner: ${data.partner_name || 'N/A'} • Items: ${data.items?.length || 0}`
        // );
      } else {
        showAlert('error', 'Failed to Load', 'Could not load delivery note for editing.');
      }
    } catch (error) {
      console.error('Failed to fetch delivery note for editing:', error);
      showAlert('error', 'Error Loading Data', 'An error occurred while loading the delivery note.', error.message);
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchBusinessDetails();
    
    if (editId) {
      // If ID is present, fetch existing delivery note for editing
      fetchDeliveryNoteForEdit(editId);
    } else {
      // If no ID, fetch next number for creating new delivery note
      fetchNextInvoiceNumber();
    }
  }, [editId]);

  // Partner search effect
  useEffect(() => {
    const fetchPartnersSuggestions = async () => {
      // Require at least 2 characters before making API request
      if (queryPartner.length < 2) {
        setSuggestionsPartner([]);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No token found for partner search');
          setSuggestionsPartner([]);
          return;
        }

        const res = await fetch(`${apiUrl}/partner-search?name=${encodeURIComponent(queryPartner)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (res.ok) {
          const data = await res.json();
          setSuggestionsPartner(data);
        } else {
          console.error('Failed to fetch partner suggestions');
        }
      } catch (error) {
        console.error('Partner fetch error:', error);
      }
    };

    const timeout = setTimeout(fetchPartnersSuggestions, 100); // debounce

    return () => clearTimeout(timeout);
  }, [queryPartner]);

  // Partner selection handler
  const handleSelectPartner = (partner) => {
    // Format bank information: combine bank name and bank number
    let bankInfo = '';
    if (partner.bank && partner.bank_number) {
      bankInfo = `${partner.bank} - ${partner.bank_number}`;
    } else if (partner.bank_number) {
      bankInfo = partner.bank_number;
    } else if (partner.bank) {
      bankInfo = partner.bank;
    }

    setInvoice({
      ...invoice,
      partner_id: partner.id, // Store partner ID for backend
      recipient: partner.name,
      recipientAddress: partner.address,
      recipientBankNo: bankInfo,
    });
    setShowPartnerSuggestions(false);
  };

  // Product search functionality
  const handleProductSearch = async (idx, value) => {
    setProductActiveRow(idx);
    handleItemChange(idx, 'name', value); // Update item.name

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found for product search');
        setSuggestionsProduct([]);
        return;
      }

      const res = await fetch(`${apiUrl}/product-search?name=${encodeURIComponent(value)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!res.ok) throw new Error('Failed to fetch product suggestions');

      const data = await res.json();
      setSuggestionsProduct(data);
      setProductActiveRow(idx);
    } catch (error) {
      console.error('Error fetching product suggestions:', error);
      setSuggestionsProduct([]);
    }
  };

  // Product selection handler
  const handleSelectProductAtIndex = (idx, product) => {
    const updatedItems = [...invoice.items];
    
    // Update the item with product information
    updatedItems[idx] = {
      ...updatedItems[idx],
      product_id: product.id,
      name: product.name,
      price: product.price || 0,
      taxRate: product.tax || 0
    };

    setInvoice({ ...invoice, items: updatedItems });
    setSuggestionsProduct([]);
    setProductActiveRow(null);
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutsideProduct = () => {
      setSuggestionsProduct([]);
      setProductActiveRow(null);
    };
    document.addEventListener('click', handleClickOutsideProduct);
    return () => document.removeEventListener('click', handleClickOutsideProduct);
  }, []);

  // Click outside handler for partner suggestions
  useEffect(() => {
    const handleClickOutsidePartner = (event) => {
      // Check if the click is outside the partner input and suggestions
      const partnerInput = event.target.closest('.partner-input-container');
      if (!partnerInput) {
        setShowPartnerSuggestions(false);
      }
    };
    document.addEventListener('click', handleClickOutsidePartner);
    return () => document.removeEventListener('click', handleClickOutsidePartner);
  }, []);

  //   useEffect(() => {
  //   const mediaQuery = window.matchMedia("print");
  //   const handlePrint = (e) => setIsPrintMode(e.matches);

  //   mediaQuery.addListener(handlePrint);
  //   return () => mediaQuery.removeListener(handlePrint);
  // }, []);

  // Delivery notes typically don't show totals/pricing

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoice.items];
    updatedItems[index][field] = field === 'quantity' || field === 'price' ? parseFloat(value) : value;
    setInvoice({ ...invoice, items: updatedItems });
    
    // Clear validation error for items when user starts fixing them
    if (validationErrors.items) {
      setValidationErrors(prev => ({ ...prev, items: undefined }));
    }
  };

  // const handlePrint = () => {
  //   window.print();
  // };

  // const handlePrint = () => {
  //   const divInvoice = document.getElementById('invoice');
  //   if (!divInvoice) return;

  //   if (!invoice.invoiceNumber) {
  //     showAlert('error', 'Missing Information', 'Delivery note number is not set!');
  //     return;
  //   }

  //   if (invoice.items.length === 0) {
  //     showAlert('error', 'Missing Items', 'You must have at least one item!');
  //     return;
  //   }

  //   const printWindow = window.open('', '_blank');

  //   const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
  //     .map((node) => node.outerHTML)
  //     .join('\n');

  //   printWindow.document.write(`
  //   <html>
  //     <head>
  //       <title>Print Invoice</title>
  //       ${styles} <!-- This copies Tailwind and other styles -->
  //       <style>
  //         @media print {
  //           body {
  //             margin: 0;
  //             padding: 20px;
  //           }
  //           input, textarea, select {
  //             border: none !important;
  //             background: transparent !important;
  //           }
  //           input::placeholder, textarea::placeholder {
  //             visibility: hidden !important;
  //           }
  //           select {
  //             -webkit-appearance: none;  /* For Safari */
  //             -moz-appearance: none;     /* For Firefox */
  //             appearance: none;          /* For modern browsers */
  //             background: transparent !important;
  //             border: none !important;
  //           }
  //           body, #invoice {
  //             height: 100vh; /* Make sure the container fills the whole page height */
  //             display: flex;
  //             flex-direction: column;
  //           }

  //           .mt-auto {
  //             margin-top: auto !important; /* Push the footer to the bottom */
  //           }

  //           .print-footer {
  //             position: absolute;
  //             bottom: 0;
  //             width: 100%;
  //           }   
  //           input[type="date"]::-webkit-calendar-picker-indicator {
  //             display: none;
  //           } 
  //           .span-days {
  //              margin-left: -19px !important;
  //           }          
  //         }
  //       </style>
  //     </head>
  //     <body>
  //       ${divInvoice.innerHTML}
  //     </body>
  //   </html>
  // `);

  //   printWindow.document.close();
  //   printWindow.focus();

  //   // Wait for styles to load, then print
  //   printWindow.onload = () => {
  //     printWindow.print();
  //     printWindow.close();
  //   };
  // };

  const handleSave = async (saveAsStatus = 'Issued') => {
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Show detailed validation message
      const errorMessages = Object.values(errors).join('\n• ');
      showAlert('error', 'Validation Error', 'Please fix the following errors:', `• ${errorMessages}`);
      return;
    }

    try {
      // Check for incomplete rows before processing
      const hasIncompleteRows = invoice.items.some(item => {
        const hasAnyData = item.name?.trim() || item.quantity;
        const isComplete = item.name?.trim() && 
                          item.quantity && 
                          parseFloat(item.quantity) > 0;
        
        return hasAnyData && !isComplete;
      });

      if (hasIncompleteRows) {
        showAlert('error', 'Incomplete Items', 'Please complete all item rows or remove empty rows before saving.');
        return;
      }

      const validItems = invoice.items.filter(item => 
        item.name?.trim() && 
        item.quantity && 
        parseFloat(item.quantity) > 0
      );

      // Check if we have a partner or need to create one
      let partnerId = invoice.partner_id;
      
      if (!partnerId && invoice.recipient?.trim()) {
        // Create or find partner based on recipient name
        const partnerData = {
          name: invoice.recipient.trim(),
          address: invoice.recipientAddress?.trim() || '',
          bank_number: invoice.recipientBankNo?.trim() || ''
        };

        const token = localStorage.getItem('token');
        if (!token) {
          showAlert('error', 'Authentication Required', 'Please login first to save delivery notes.');
          return;
        }

        // Try to create partner
        const partnerResponse = await fetch(`${apiUrl}/partner-add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(partnerData),
        });

        if (partnerResponse.ok) {
          const partnerResult = await partnerResponse.json();
          partnerId = partnerResult.id;
        } else {
          // Partner creation failed, might already exist - that's ok, we'll use default partner (1)
          partnerId = 1;
        }
      }

      // Sanitize items data to ensure no circular references
      const sanitizedItems = validItems.map(item => {
        // Only extract primitive values, avoid any object references
        const sanitizedItem = {
          name: String(item.name || '').trim(),
          quantity: Number(item.quantity) || 0,
          note: String(item.note || '').trim()
        };

        // Only include product_id if it's a valid number
        if (typeof item.product_id === 'number' && item.product_id > 0) {
          sanitizedItem.product_id = item.product_id;
        }

        return sanitizedItem;
      });

      // Prepare delivery note data
      const dnoteData = {
        partner_id: Number(partnerId) || 1,
        dnote_number: String(invoice.invoiceNumber || '').trim(),
        date: String(invoice.date || ''),
        status: String(saveAsStatus),
        note: String(invoice.note || '').trim(),
        items: sanitizedItems
      };

      console.log('Sending delivery note data to backend:', dnoteData);

      const token = localStorage.getItem('token');
      
      let requestBody;
      try {
        requestBody = JSON.stringify(dnoteData);
      } catch (stringifyError) {
        console.error('Error serializing delivery note data:', stringifyError);
        console.error('Problematic data:', dnoteData);
        throw new Error('Failed to prepare delivery note data for sending. Please check for invalid data.');
      }
      
      // Choose endpoint and method based on whether we have an edit ID
      const endpoint = editId 
        ? `${apiUrl}/dnote-edit/${editId}`
        : `${apiUrl}/dnote-add`;
      const method = editId ? 'PATCH' : 'POST';
      
      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: requestBody,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Delivery note saved successfully:', result);

      // Store the delivery note ID for new saves (for PDF generation)
      if (!editId && result.id) {
        setSavedDeliveryNoteId(result.id);
      }

      // Generate and save PDF after successful database save
      const deliveryNoteId = editId || result.id;
      if (deliveryNoteId) {
        try {
          // Generate and upload PDF to server
          await handlePrintNote(invoice, showAlert, closeAlert, 'delivery', true, deliveryNoteId);
        } catch (pdfError) {
          console.error('PDF generation failed:', pdfError);
          // Continue with success message even if PDF generation fails
        }
      }

      // Clear validation errors after successful save
      setValidationErrors({});

      const statusText = saveAsStatus === 'Pending' ? 'draft' : 'final';
      const actionText = editId ? 'updated' : 'saved';
      showAlert('success', 'Delivery Note Saved!', `Delivery note #${dnoteData.dnote_number} ${actionText} successfully as ${statusText}!`, `System ID: ${result.id || editId} • PDF generated and saved to server.`);
      
      // Only refresh number if not in edit mode
      if (!editId) {
        fetchNextInvoiceNumber();
      }

    } catch (error) {
      console.error('Error saving delivery note:', error);
      
      // Determine user-friendly message based on error type
      let userMessage = editId ? 'Failed to update delivery note' : 'Failed to save delivery note';
      if (error.message.includes('duplicate') || error.message.includes('already exists')) {
        userMessage = 'Duplicate number detected';
      } else if (error.message.includes('login') || error.message.includes('token')) {
        userMessage = 'Authentication error';
      } else if (error.message.includes('partner')) {
        userMessage = 'Partner creation failed';
      }
      
      showAlert('error', 'Save Failed', userMessage, error.message);
    }
  };

  const [KomercijalistEnabled, setKomercijalistEnabled] = useState(true);
  const [PodignalEnabled, setPodignalEnabled] = useState(true);
  const [voziloEnabled, setVoziloEnabled] = useState(true);

  // State toggle functions
  // const toggleKomercijalistEnabled = () => setKomercijalistEnabled(!KomercijalistEnabled);
  const toggleKomercijalistEnabled = () => {
    setKomercijalistEnabled((prev) => {
      const newState = !prev;
      console.log('KomercijalistEnabled changed:', newState); // Log the new state
      return newState;
    });
  };
  const togglePodignalEnabled = () => setPodignalEnabled(!PodignalEnabled);
  const toggleVoziloEnabled = () => setVoziloEnabled(!voziloEnabled);

  const handleRemoveRow = (index) => {
    const updatedItems = invoice.items.filter((_, i) => i !== index);
    setInvoice((prev) => ({
      ...prev,
      items: updatedItems,
    }));
  };

  const handleAddRow = () => {
    const newItem = {
      id: Date.now(),
      name: '',
      quantity: 1,
    };

    setInvoice((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const [reklamacijeEnabled, setReklamacijeEnabled] = useState(false);
  const toggleNote = () => {
    setReklamacijeEnabled((prev) => !prev);
  };

  const [footerEnabled, setfooterEnabled] = useState(true);
  const toggleFooter = () => {
    setfooterEnabled((prev) => !prev);
  };

  // View mode state - default to advanced (same pattern as InvoiceGenerator)
  const [viewMode, setViewMode] = useState('advanced');

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    handlePrint: handlePrintClick,
    handleSave,
    handleAddRow,
    toggleNote,
    toggleFooter,
    toggleKomercijalistEnabled,
    togglePodignalEnabled,
    toggleVoziloEnabled,
    setViewMode,
    populateDeliveryNoteData,
    // Expose state values
    reklamacijeEnabled,
    footerEnabled,
    KomercijalistEnabled,
    PodignalEnabled,
    voziloEnabled,
    viewMode
  }));

  return (
    <div className="flex w-full">
      <div id="invoice" className="flex-1 w-[704px] min-h-[1023px] bg-white p-8 rounded shadow flex flex-col pt-0">
        {/* Header: Logo only - only show in advanced mode */}
        {viewMode === 'advanced' && (
          <div className="flex flex-col items-center mb-5 border-b border-gray-200 pb-2">
            <img src={invoice.logo} alt="Company Logo" className="w-52 object-contain" />
            <p className="text-xs font-medium text-gray-700 -mt-2.5">
              {businessDetails.full_name || 'Business Name'}
            </p>
          </div>
        )}

        {/* Header - with added space on top only in basic mode */}
        <div id="header" className={`flex justify-between mb-6 ${viewMode === 'basic' ? 'mt-8' : 'mt-2'}`}>
        <div className="space-y-1 w-1/2 border border-gray-400 rounded p-2">
            <div className="flex items-start">
              <label className="text-sm font-medium w-10">До:</label>
              <div className="relative w-full partner-input-container">
                <input
                  type="text"
                  value={invoice.recipient}
                  onChange={(e) => {
                    setInvoice({ ...invoice, recipient: e.target.value });
                    setQueryPartner(e.target.value);
                    // Only show suggestions if there's at least 2 characters
                    setShowPartnerSuggestions(e.target.value.trim().length >= 2);
                    if (validationErrors.recipient) {
                      setValidationErrors(prev => ({ ...prev, recipient: undefined }));
                    }
                  }}
                  className={`w-full p-1 border rounded text-sm resize-none font-bold ${
                    validationErrors.recipient ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Recipient"
                  rows={2} 
                />

                {/* Partner dropdown suggestions */}
                {showPartnerSuggestions && suggestionsPartner.length > 0 && (
                  <ul className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded shadow-md max-h-48 overflow-auto text-sm z-50">{suggestionsPartner.map((partner) => (
                      <li
                        key={partner.id}
                        onClick={() => {
                          handleSelectPartner(partner);
                        }}
                        className="p-2 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:border-l-4 hover:border-blue-500 hover:shadow-sm cursor-pointer transition-all duration-200 ease-in-out"
                      >
                        <div className="font-medium">{partner.name}</div>
                        {partner.address && (
                          <div className="text-xs text-gray-600">{partner.address}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="flex items-start">
              <label className="text-sm font-medium w-10"></label>
              <input
                type="text"
                value={invoice.recipientAddress}
                onChange={(e) => setInvoice({ ...invoice, recipientAddress: e.target.value })}
                className="w-full p-1 border border-gray-300 rounded text-sm"
                placeholder="Recipient Address"
              />
            </div>
            <div className="flex items-center">
              <label className="text-sm font-medium w-10"></label>
              <input
                type="text"
                value={invoice.recipientBankNo}
                onChange={(e) => setInvoice({ ...invoice, recipientBankNo: e.target.value })}
                className="w-full p-1 border border-gray-300 rounded text-sm"
                placeholder="Bank - Account Number"
              />
            </div>
          </div>

          {/* Right side with fixed labels aligned to the end */}
          <div className="space-y-2 w-fit text-right">
            {viewMode === 'advanced' && (
              <div className="grid grid-cols-2 gap-x-4">

                <label className="text-sm">ЕДБ</label>
                <span className="text-sm">{businessDetails.tax_id }</span>
              </div>
            )}

            <div className={`grid grid-cols-2 items-center gap-x-4 `}>
              <label className="text-sm">Датум</label>
              <input
                type="date"
                value={invoice.date}
                onChange={(e) => setInvoice({ ...invoice, date: e.target.value })}
                className="text-sm text-gray-700 border border-gray-300 rounded"
              />
            </div>

            {viewMode === 'advanced' && (
              <div className="grid grid-cols-2 items-center gap-x-4">
                <label className="text-sm">Датум Валута</label>
                <input
                  type="date"
                  value={invoice.date}
                  onChange={(e) => setInvoice({ ...invoice, date: e.target.value })}
                  className="text-sm text-gray-700 border border-gray-300 rounded"
                />
              </div>
            )}
          </div>



        </div>

        {/* invoice number */}
        <div className="w-full flex justify-center items-center mt-4 mb-2">
          <div className="flex items-center justify-center">
              <label htmlFor="invoiceNumber" className="text-lg text-gray-700 font-bold mr-1">
                Испратница #
              </label>
              <input
                type="text"
                id="invoiceNumber"
                name="invoiceNumber"
                className={`border px-2 py-1 rounded text-center w-24 font-bold print-hidden ${
                  validationErrors.invoiceNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="001"
                value={invoice.invoiceNumber}
                onChange={(e) => {
                  setInvoice({ ...invoice, invoiceNumber: e.target.value });
                  if (validationErrors.invoiceNumber) {
                    setValidationErrors(prev => ({ ...prev, invoiceNumber: undefined }));
                  }
                }}
              />
          </div>
        </div>

        <table className="w-full mb-1 border-t1 border-b-2 border border-gray-400 rounded">
          <thead>
            <tr className="text-left border-b text-xs">
              <th className="py-2 w-8 text-center">#</th>
              <th className="py-2">Назив на артикал</th>
              <th className="py-2 w-16 text-right">EM</th>
              <th className="py-2 w-24 text-right">Количина</th>
              <th className="py-1 w-10 text-right print-hidden"></th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, idx) => {
              const isItemValid = item.name?.trim() && 
                                  item.quantity && 
                                  parseFloat(item.quantity) > 0;
              
              const hasAnyData = item.name?.trim() || item.quantity;
              const isIncompleteRow = hasAnyData && !isItemValid;
              
              const showItemError = validationErrors.items && (isIncompleteRow || !isItemValid);
              
              return (
              <tr key={item.id} className="border border-gray-300 mb-1">
                <td className="p-2 text-xs  align-middle">{idx + 1}</td> {/* Row number */}
                <td className="relative">
                  <textarea
                    value={item.name}
                    placeholder="Product Name"
                    onChange={(e) => handleProductSearch(idx, e.target.value)}
                    className={`w-full p-1 text-xs resize-none leading-tight ${
                      showItemError ? 'bg-red-50 border-red-500' : ''
                    }`}
                    // style={{ fontSize: '12px' }}
                    rows={2}
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  {/* Product suggestions dropdown */}
                  {productActiveRow === idx && suggestionsProduct.length > 0 && (
                    <ul className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded shadow-md max-h-32 overflow-auto text-xs z-50">
                      {suggestionsProduct.map((product, productIdx) => (
                        <li
                          key={product.id || productIdx}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectProductAtIndex(idx, product);
                          }}
                          className="p-1 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:border-l-4 hover:border-blue-500 hover:shadow-sm cursor-pointer transition-all duration-200 ease-in-out"
                        >
                          <div className="font-medium">{product.name}</div>
                          {product.price && (
                            <div className="text-gray-600">Price: {product.price}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td>
                  <input type="text" value="пар" className="w-full text-xs text-right"/>
                </td>
                <td>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={Math.floor(item.quantity)}
                    onChange={(e) => handleItemChange(idx, 'quantity', Math.floor(Number(e.target.value)))}
                    className={`w-full text-right ${
                      showItemError ? 'bg-red-50 border-red-500' : ''
                    }`}
                  />
                </td>
                <td className="p-1 print:hidden">
                  <button
                    onClick={() => handleRemoveRow(idx)}
                    className="text-red-500 hover:text-red-700 text-sm p-0 m-0 w-4 h-4 leading-none print-hidden"
                    title="Remove row"
                  >
                    ×
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {/* Total Calculation */}
        <div id="calculation-1" className="flex justify-between text-sm">
          {/* Left side: Additional Details - only show in advanced mode */}
          {viewMode === 'advanced' && (
            <div className="text-left space-y-1 text-sm">
              <div className="flex items-center gap-1">
                <span className="font-medium w-36">Начин на плаќање</span>
                <select className="border border-gray-300 rounded text-xs h-7 w-40 text-gray-700">
                  <option value="invoice">Вирмански</option>
                  <option value="invoice">фактурно</option>
                  <option value="cash">профактурно</option>
                </select>
              </div>

              {KomercijalistEnabled && (
                <div className="flex items-center gap-1">
                  <span className="font-medium w-36">Комерцијалист</span>
                  <input type="text" className="border border-gray-300 rounded text-xs w-48 h-6 px-1" placeholder="#" />
                </div>
              )}

              {PodignalEnabled && (
                <div className="flex items-center gap-1">
                  <span className="font-medium w-36">Подигнал</span>
                  <input
                    type="text"
                    className="border border-gray-300 rounded text-xs w-48 h-6 px-1"
                    placeholder="Name"
                  />
                </div>
              )}

              {voziloEnabled && (
                <div className="flex items-center gap-1">
                  <span className="font-medium w-36">Возило</span>
                  <input type="text" className="border border-gray-300 rounded text-xs w-48 h-6 px-1" placeholder="#" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Note section - controlled by reklamacijeEnabled toggle and only show in advanced mode */}
        {reklamacijeEnabled && viewMode === 'advanced' && (
          <div id="note-section" className="mt-4 p-4 border border-gray-300 rounded shadow-md text-xs bg-gray-50">
            <div className="mb-2">
              <b>Напомена:</b>
            </div>
            <textarea
              value={invoice.note || 'Робата е предадена во исправна состојба. Купувачот е должен да ја провери при приемот. Со потпишување на испратницата, купувачот ја потврдува исправноста и количината на примена роба. Сите евентуални рекламации се прифаќаат само при прием.'}
              onChange={(e) => setInvoice({ ...invoice, note: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded text-xs bg-white resize-none"
              placeholder="Внесете текст за рекламации..."
              rows={3}
            />
          </div>
        )}

        {/* Footer - only show in advanced mode */}
        {viewMode === 'advanced' && (
 <div className="mt-auto print-footer">
    {/* --- SIGNATURES SECTION (Stays the same, it's correct) --- */}
    <div id="footer-1" className="flex w-full justify-between items-end mb-4">
        {/* Left Signature Block */}
        <div className="w-1/2">
            <div className="w-2/3 ml-10">
                <span className="text-[12px] text-gray-600">__.__.2025</span>
                <div className="border-t border-gray-400 mt-1 mb-1"></div>
                <span className="text-[10px] text-gray-600 w-full block text-center">Примил</span>
            </div>
        </div>
        {/* Right Signature Block */}
        <div className="w-1/2">
            <div className="w-2/3 ml-auto mr-10 text-center">
                <div className="border-t border-gray-400 w-full mb-1"></div>
                <span className="text-[10px] text-gray-600">Издал</span>
            </div>
        </div>
    </div>

    {/* --- BUSINESS DETAILS SECTION (The Final, Corrected Code) --- */}
    {footerEnabled && (
        // 1. Establish a positioning context for its children.
        //    A min-height ensures the container doesn't collapse.
        <div id="footer-2" className="w-full relative text-xs text-gray-700 leading-tight mt-2" style={{ minHeight: '64px' }}>

            {/* Address (Left) - This element remains in the normal flow */}
            <div className="text-left">
                <p>{`${businessDetails.address}, ${businessDetails.city}`}</p>
                 <p>
                    <a href={businessDetails.website || 'http://www.elkos.mk'} target="_blank" rel="noopener noreferrer">
                        {businessDetails.website || 'www.elkos.mk'}
                    </a> | 
                    email: <a href={`mailto:${businessDetails.email || 'info@elkos.mk'}`}>
                        {businessDetails.email || 'info@elkos.mk'}
                    </a>
                </p>

                <p>тел: {businessDetails.phone || '071562449'}</p>
            </div>

            {/* QR Code (Right) - This is taken out of the flow and pinned to the right */}
            {/* 2. Position this element absolutely relative to the parent above. */}
            <div className="absolute right-0 top-0">
                <QRCodeGenerator
                    data={(() => {
                      const deliveryNoteId = editId || savedDeliveryNoteId;
                      const companyId = "1"; // Default company ID - could be made dynamic later
                      
                      if (deliveryNoteId) {
                        // New URL structure with company/folder/filename
                        const cleanFilename = `Испратница-${invoice.invoiceNumber}_${new Date().toISOString()}.pdf`;
                        return `https://pulse-iota-seven.vercel.app/preview/${companyId}/dnote/${encodeURIComponent(cleanFilename)}`;
                      } else {
                        // Fallback to old structure
                        return `https://pulse-iota-seven.vercel.app/preview/${encodeURIComponent(`delivery-note-${invoice.invoiceNumber}.pdf`)}`;
                      }
                    })()}
                    size={64}
                    className="w-16 h-16 object-contain"
                />
            </div>

        </div>
    )}
</div>
        )}
      </div>

      {/* Sidebar */}
      {!hideSidebar && (
      <div className="w-48 p-4 bg-gray-50 border-l flex flex-col gap-2 print:hidden">
        {/* Advanced Mode Toggle */}
        <div className="mb-2">
          <div className="text-xs text-gray-600 mb-1 font-medium text-center">Advanced Mode</div>
          <div className="flex bg-gray-200 rounded-lg p-1 shadow-sm border">
            <button
              onClick={() => setViewMode('basic')}
              className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'basic'
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              OFF
            </button>
            <button
              onClick={() => setViewMode('advanced')}
              className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'advanced'
                  ? 'bg-green-500 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              ON
            </button>
          </div>
        </div>

        <hr className="my-2 border-gray-200" />

        {/* <button
          onClick={handlePreview}
          className="w-full bg-white border border-gray-300 text-gray-800 py-2 rounded hover:bg-gray-100"
        >
          Preview
        </button> */}
        <button
          onClick={handlePrintClick}
          className="w-full bg-white border border-gray-300 text-gray-800 py-2 rounded hover:bg-gray-100"
        >
          Print
        </button>
        {/* Hidden: Save and Create Invoice button
        <button
          onClick={handleSaveDraft}
          className="w-full bg-yellow-100 text-yellow-900 py-2 rounded hover:bg-yellow-200"
        >
          Save and Create Invoice
        </button>
        */}
        <button onClick={handleSave} className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700">
          Save
        </button>
        {/* <button
          onClick={handleSaveAndPrint}
          className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
        >
          Save and Print
        </button> */}
        {/* Divider line */}
        <hr className="my-4 border-t border-gray-300" />

        {/* Settings section */}
        <div>
          <div className="text-sm font-semibold text-gray-600 mb-2">Settings</div>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleAddRow}
              className="w-full bg-gray-200 text-gray-800 text-sm py-1 rounded hover:bg-gray-300"
            >
              + Add row
            </button>
          </div>
        </div>
      </div>
      )}
      
      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        details={alert.details}
      />
    </div>
  );
});

export default DeliveryNote;